import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import React, { useState } from 'react'
import { isAiClaimsPortalPath } from '../config/aiclaims-routes'
import {
  navigateToAllowedBrandHome,
  resolvePostLoginPath,
} from '../lib/crossDomainAuth'
import { effectiveBrandAccess } from '../lib/pageAccess'
import { LoginApi, type LoginResponse, OtpApi, UsersApi } from '../services'

interface LoginData {
  username: string
  password: string
}

function navigateAfterLogin(
  currentUser?: {
    brandAccess?: string | null
    rolenames?: readonly string[] | null
  } | null,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const brandAccess = effectiveBrandAccess(currentUser)
  const path = resolvePostLoginPath(brandAccess, window.location.hostname)

  if (
    isAiClaimsPortalPath(path) ||
    path === '/homehub' ||
    path.startsWith('/homehub/')
  ) {
    navigateToAllowedBrandHome(brandAccess, path)
    return
  }

  window.location.href = path
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const [otpError, setOtpError] = useState<string | null>(null)
  const [requiresOtp, setRequiresOtp] = useState<boolean>(false)

  const router = useRouter()
  const queryClient = useQueryClient()

  // Use React Query to manage effectivePartnerType state globally
  const { data: effectivePartnerType, isLoading: partnerTypeLoading } =
    useQuery({
      queryKey: ['effectivePartnerType'],
      queryFn: () => {
        // Initialize from localStorage, but don't default to 'source' yet
        // Let the user data fetch set the proper default based on their roles
        if (typeof window !== 'undefined') {
          const stored = localStorage.getItem('effectivePartnerType')
          return stored || null
        }
        return null
      },
      staleTime: Number.POSITIVE_INFINITY, // Don't refetch automatically
      refetchOnWindowFocus: false,
      enabled: false, // Disable automatic fetching to prevent infinite loops
      initialData: (() => {
        // Only run this once during initialization
        if (typeof window !== 'undefined') {
          return localStorage.getItem('effectivePartnerType') || null
        }
        return null
      })(),
    })

  const resetOtpRequirement = () => setRequiresOtp(false)

  const {
    data: user,
    isLoading,
    error: userError,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      console.log('🔍 API Call: Fetching user data from /api/v1/users/me')
      const response = await new UsersApi().readUserMe()
      return response
    },
    enabled: !requiresOtp,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - override default for user data
    refetchOnWindowFocus: false, // Don't refetch user data when window regains focus
  })

  // Handle effectivePartnerType update when user data changes (TanStack Query v5 approach)
  React.useEffect(() => {
    if (user) {
      const databasePartnerType = user.effectivePartnerType

      // Prioritize System Administrator role over partner types
      let defaultPartnerType: string
      if (databasePartnerType) {
        defaultPartnerType = databasePartnerType
      } else if (user.rolenames?.includes('System Administrator')) {
        defaultPartnerType = 'System Administrator'
      } else {
        // Fall back to first role or 'source'
        defaultPartnerType = user.rolenames?.[0] || 'source'
      }

      console.log(
        '✅ Setting effectivePartnerType from database:',
        defaultPartnerType,
        'User roles:',
        user.rolenames,
      )

      // Always update localStorage and React Query cache with database value
      if (typeof window !== 'undefined') {
        localStorage.setItem('effectivePartnerType', defaultPartnerType)
      }
      // Set in React Query cache for global state management
      queryClient.setQueryData(['effectivePartnerType'], defaultPartnerType)
    }
  }, [user, queryClient])

  const loginMutation = useMutation<LoginResponse, any, LoginData>({
    mutationFn: async (data: LoginData) => {
      const response = await new LoginApi().loginAccessToken({
        formData: { username: data.username, password: data.password },
      })
      console.log('🔍 Login response:', response)
      console.log('🔍 requiresOtp value:', response.requiresOtp)
      console.log('🔍 response keys:', Object.keys(response))
      return response
    },
    onSuccess: async (response) => {
      // Handle both camelCase and snake_case property names
      const requiresOtp =
        (response as any).requires_otp ??
        (response as any).requiresOtp ??
        (response as any).requiresotp ??
        false
      console.log('✅ onSuccess - requires_otp:', requiresOtp)
      if (requiresOtp) {
        setRequiresOtp(true)
      } else {
        setRequiresOtp(false)
        console.log('🔄 Login successful, refreshing user data...')
        // Refetch user data and wait for it to complete
        await queryClient.refetchQueries({ queryKey: ['currentUser'] })
        const currentUser = queryClient.getQueryData(['currentUser']) as
          | {
              brandAccess?: string | null
              rolenames?: readonly string[] | null
            }
          | undefined
        console.log('✅ User data refreshed, navigating after login')
        navigateAfterLogin(currentUser)
      }
    },
    onError: (err: any) => {
      // Check if error contains a response and detail field
      const errorMessage =
        err.response?.detail || 'An unknown error occurred. Please try again.'
      setError(errorMessage)
    },
  })

  const verifyOtpMutation = useMutation({
    mutationFn: async ({
      userId,
      otpCode,
      loginId,
    }: {
      userId: number
      otpCode: number
      loginId: number
    }) => {
      const response = await new OtpApi().validateOtp({
        userId,
        otp: otpCode,
        loginid: loginId,
      })
      return response
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      await queryClient.refetchQueries({ queryKey: ['currentUser'] })
      setRequiresOtp(false)
      const currentUser = queryClient.getQueryData(['currentUser']) as
        | {
            brandAccess?: string | null
            rolenames?: readonly string[] | null
          }
        | undefined
      navigateAfterLogin(currentUser)
    },
    onError: () => {
      setOtpError('OTP verification failed. Please try again.')
    },
  })

  const handleOtpSubmit = async (
    userId: number,
    otpCode: string,
    loginId: number,
  ) => {
    setOtpError(null)
    try {
      await verifyOtpMutation.mutateAsync({
        userId,
        otpCode: Number(otpCode), // Ensure otpCode is a number
        loginId,
      })
    } catch (_err) {
      setOtpError('OTP verification failed. Please try again.')
    }
  }
  const verifyUnlockToken = async (token: string) => {
    try {
      // Use fetch to call the public unlock endpoint directly
      const response = await fetch(
        `/api/v1/accountunlock/unlock-account?token=${token}`,
        {
          method: 'GET',
          redirect: 'manual', // Don't follow redirects automatically
        },
      )

      if (response.ok || response.status === 302) {
        // Success - redirect to login
        router.push('/login')
      } else {
        throw new Error('Unlock verification failed')
      }
    } catch (error: any) {
      console.error('Failed to verify unlock token:', error)
      setError('Unlock verification failed. Please request a new unlock email.')
    }
  }

  const logout = async () => {
    console.log('🚪 Logout function called')
    try {
      console.log('📡 Calling logout API endpoint')
      await new LoginApi().logout()
      console.log('✅ Logout API call successful')
    } catch (error: any) {
      console.error('❌ Logout API call failed:', error)
      // Continue with logout even if the API call fails
      // This ensures user can still log out if there's a network issue
    } finally {
      // Always clear the local state and redirect, regardless of API success/failure
      console.log('🧹 Clearing user data and cache')

      // Explicitly set user data to undefined
      queryClient.setQueryData(['currentUser'], undefined)

      // Invalidate and remove the user query
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      queryClient.removeQueries({ queryKey: ['currentUser'] })

      // Clear all cached data
      queryClient.clear()

      if (typeof window !== 'undefined') {
        localStorage.removeItem('effectivePartnerType')
        localStorage.removeItem('access_token')
      }

      console.log('🔄 Redirecting to home page')
      router.replace('/') // Use replace instead of push to prevent back button issues
    }
  }

  return {
    loginMutation,
    handleOtpSubmit,
    otpError,
    requiresOtp,
    logout,
    user,
    isLoading: isLoading || partnerTypeLoading, // Include both loading states
    error,
    userError,
    effectivePartnerType,
    setEffectivePartnerType: (value: string | null) => {
      console.log('🔧 setEffectivePartnerType called with:', value)
      // Set in React Query cache for global state management
      queryClient.setQueryData(['effectivePartnerType'], value)
      if (typeof window !== 'undefined') {
        if (value) {
          localStorage.setItem('effectivePartnerType', value)
          console.log('💾 Saved to localStorage:', value)
        } else {
          localStorage.removeItem('effectivePartnerType')
          console.log('🗑️ Removed from localStorage')
        }
      }
      // No need to invalidate since we're directly setting the data
      console.log('✅ Updated effectivePartnerType in cache and localStorage')
    },
    resetError: (message: string | null = null) => setError(message),
    resetOtpRequirement,
    verifyUnlockToken,
    setOtpError,
  }
}

export default useAuth
